import React, { Fragment, memo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Paper, Portal } from '@mui/material';
import MapCanvasMiniature from './MapCanvasMiniature';
import { SATELLITE_LAYERS } from 'js/constants/SatelliteLayers';
import AnalysisMapView from 'js/components/AnalysisMapView/AnalysisMapView';
import { getLayerConfig } from 'js/components/Prescription/PrescriptionUtils';
import { ClearRounded } from '@mui/icons-material';
import ButtonBase from '@mui/material/ButtonBase';
import { useLangFile } from 'js/context/LanguageContext';
import { getTooltip } from './MapCanvasTooltip';

const SIZE = 180;
const PADDING = 8;

import Styles from './MapCanvasMiniature.module.less';
import { useGoogleMapDOM } from 'js/context/GoogleMapContext';
import { MANUAL_FEATURE_FLAGS } from 'js/featureFlags/featureFlags';
import ViewModeConstants from 'js/constants/ViewModeConstants';
import { isSatelliteLayer, isSurveyLayer, isYieldLayer } from '../../Prescription/PrescriptionJob';
import { usePrescriptionJob } from '../../Prescription/PrescriptionJobContext';
import { validateLayerOrGetValidConfiguration } from '../../../datacontainers/SurveyContainer';
import { useSurveyContext, useSurveyLayerViewCapabilities } from '../../../context/SurveyContext';
import { getDemandTooltip } from './MapCanvasDemandTooltip';
import { getLayerCategory } from '../../../helpers/SurveyUtils';
import { useSurveyCountryCode } from '../../../hooks/useSurveyCountryCode';

const getMaxHeight = (googleMapRef, legendRef) => {
  let maxHeight = null;
  const padding = 24;

  if (googleMapRef && legendRef) {
    const mH = googleMapRef.getBoundingClientRect().height;
    const pH = legendRef.previousSibling.getBoundingClientRect().height;

    maxHeight = mH - pH - padding;
  }

  return maxHeight;
};

const MapCanvasMiniatureContainer = (props: MapCanvasMiniatureContainer.propTypes) => {
  const googleMapRef = useGoogleMapDOM();
  const countryCode = useSurveyCountryCode();

  const LangFile = useLangFile();
  const layerCapabilities = useSurveyLayerViewCapabilities(
    props.selectedLayer,
    ViewModeConstants.PRESCRIPTION
  );
  const [target, setTarget] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [error, setError] = useState(false);

  const { surveyViewCapabilities, selectedSurvey } = useSurveyContext();
  const { prescriptionJob } = usePrescriptionJob();

  const handleRemove = useCallback(
    (layer) => {
      const result = props.multipleSelections.filter((item) => item !== layer);
      props.onMultipleSelectChanged(result);
    },
    [props.multipleSelections, props.onMultipleSelectChanged]
  );

  const maxHeight = getMaxHeight(googleMapRef, props.legendRef);

  const imageUrl = useCallback(
    (layer) => {
      const config = getLayerConfig(layer);

      if (isSatelliteLayer(layer)) {
        return prescriptionJob.assets[layer].replace(/\d{4}-\d{2}-\d{2}/, props.selectedDate);
      }
      if (isSurveyLayer(layer)) {
        const survey = props.surveysRef.find(
          (survey) =>
            Object.keys(survey.newImages).includes(layer) &&
            survey.fieldId === prescriptionJob.fieldId
        );

        const validConfig = validateLayerOrGetValidConfiguration(
          layer,
          survey,
          props.viewMode,
          surveyViewCapabilities,
          props.classificationsEnabled,
          props.variationsEnabled
        );
        if (validConfig === true) {
          return config.getImageUrl(
            survey.newImages,
            props.classificationsEnabled,
            props.variationsEnabled,
            ViewModeConstants.PRESCRIPTION
          );
        } else if (validConfig !== false) {
          return config.getImageUrl(
            prescriptionJob.assets,
            validConfig.classificationsEnabled,
            validConfig.variationsEnabled,
            ViewModeConstants.PRESCRIPTION
          );
        } else {
          setError(true);
          return null;
        }
      }
      if (isYieldLayer(layer)) {
        const yieldAssets = [...prescriptionJob.assets[layer].images];
        const yieldSortedByDate = yieldAssets.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        return yieldSortedByDate[0].url;
      } else {
        return null;
      }
    },
    [prescriptionJob, props, selectedSurvey]
  );

  return (
    <Fragment>
      {MANUAL_FEATURE_FLAGS.PRESCRIPTION_MINIATURES &&
        props.legendRef &&
        props.multipleSelections.length > 0 &&
        !props.hideReferenceMaps && (
          <Portal container={props.legendRef}>
            <Box
              className={Styles.Root}
              sx={{
                mt: 1,
                width: SIZE,
                maxHeight: `${maxHeight}px`,
                maxWidth: SIZE + 2 * PADDING + 24,
              }}
            >
              <Paper className={Styles.Paper} elevation={2} style={{ padding: PADDING }}>
                {props.multipleSelections.map((layer, idx, arr) => {
                  const tooltip =
                    props.classificationsEnabled && layerCapabilities.enableCustomClassification
                      ? // getDemandTooltip returns an array of rows; the
                        // miniature only renders the primary (first) row.
                        getDemandTooltip(
                          layer,
                          props.values,
                          target,
                          props.classificationValues,
                          props.classificationsEnabled,
                          props.variationsEnabled,
                          LangFile,
                          countryCode
                        )[0]
                      : getTooltip(
                          layer,
                          props.values,
                          target,
                          props.classificationsEnabled,
                          props.variationsEnabled,
                          LangFile
                        );

                  const showTargetAndValues =
                    getLayerCategory(props.selectedLayer) == getLayerCategory(layer);

                  const hideTooltipValue = layer === SATELLITE_LAYERS.VISIBLE;

                  return (
                    <Box
                      data-testid={`map-miniature-${layer}`}
                      data-value={tooltip.value == null ? undefined : tooltip.value}
                      data-unit={tooltip.unit == null ? undefined : tooltip.unit}
                      key={layer}
                      sx={{
                        position: 'relative',
                        maxWidth: SIZE,
                        mt: idx === 0 ? 0 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Box
                          onMouseEnter={() => setHovered(layer)}
                          onMouseLeave={() => setHovered(null)}
                          sx={{
                            minHeight: '24px',
                            fontSize: '0.75rem',
                            lineHeight: '0.75rem',
                            width: '100%',
                            display: 'inline-flex',
                            flexWrap: 'nowrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          {hovered === layer && (
                            <ButtonBase
                              className={Styles.CloseButton}
                              focusRipple={true}
                              onClick={() => handleRemove(layer)}
                            >
                              <ClearRounded fontSize={'small'} />

                              {LangFile.MapCanvasMiniature.hide}
                            </ButtonBase>
                          )}

                          {hovered !== layer && (
                            <Box
                              style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                              sx={{
                                pr: 1,
                                maxWidth: '60%',
                              }}
                            >
                              {tooltip.text}
                            </Box>
                          )}

                          {hovered !== layer && showTargetAndValues && (
                            <Box
                              style={{ whiteSpace: 'nowrap' }}
                              sx={{
                                pl: 1,
                                height: '100%',
                                alignItems: 'center',
                                display: 'flex',
                              }}
                            >
                              {target && !hideTooltipValue && (
                                <span>
                                  {' '}
                                  {tooltip.value}
                                  {tooltip.unit}
                                </span>
                              )}

                              <span
                                style={{
                                  paddingLeft: 4,
                                  display: tooltip.icon ? 'initial' : 'none',
                                }}
                              />

                              {props.classificationsEnabled && tooltip.icon}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          maxHeight: SIZE,
                          width: '100%',
                          my: 1,
                        }}
                      >
                        <MapCanvasMiniature
                          error={error}
                          imageURL={imageUrl(layer)}
                          target={target}
                          maxHeight={SIZE - PADDING * 2}
                          maxWidth={SIZE - PADDING * 2}
                          showTarget={showTargetAndValues}
                        />
                      </Box>

                      {idx < arr.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          </Portal>
        )}

      <AnalysisMapView
        viewMode={props.viewMode}
        imageURL={props.imageURL}
        selectedField={props.field}
        selectedLayer={props.selectedLayer}
        multipleSelections={props.multipleSelections}
        classificationsEnabled={props.classificationsEnabled}
        variationsEnabled={props.variationsEnabled}
        enableTooltip={props.selectedLayer !== SATELLITE_LAYERS.VISIBLE && !props.isOverriding}
        onTargetChanged={setTarget}
        clickable={false}
        primaryValues={props.values[props.selectedLayer]}
        values={props.values}
        classificationValues={props.classificationValues}
        countryCode={countryCode}
      />
    </Fragment>
  );
};

MapCanvasMiniatureContainer.propTypes = {
  legendRef: PropTypes.any,
  viewMode: PropTypes.string,
  imageURL: PropTypes.string,
  field: PropTypes.object,
  values: PropTypes.object,
  fieldImages: PropTypes.object,
  selectedLayer: PropTypes.string,
  multipleSelections: PropTypes.array,
  classificationsEnabled: PropTypes.bool,
  variationsEnabled: PropTypes.bool,
  isOverriding: PropTypes.bool,
  onMultipleSelectChanged: PropTypes.func,
  classificationValues: PropTypes.object,
  countryCode: PropTypes.string,
  surveysRef: PropTypes.any,
  hideReferenceMaps: PropTypes.bool,
};

export default memo(MapCanvasMiniatureContainer);
